import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../ui/command';
import { X, ChevronsUpDown, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useState } from 'react';
import { propertyCatalog, getOperatorsForType, propertyGroupLabels } from '../../../data/rulesData';
import type { PropertyGroup, PropertyDefinition } from '../../../data/rulesData';
import type { Condition } from '../../../types/rule.types';

interface ConditionRowProps {
  condition: Condition;
  onChange: (updated: Condition) => void;
  onRemove: () => void;
  isOnly: boolean;
}

export default function ConditionRow({ condition, onChange, onRemove, isOnly }: ConditionRowProps) {
  const [propertyOpen, setPropertyOpen] = useState(false);

  const selectedProp = propertyCatalog.find(p => p.id === condition.propertyId);
  const applicableOps = selectedProp ? getOperatorsForType(selectedProp.type) : [];
  const selectedOp = applicableOps.find(o => o.id === condition.operator);

  const groupedProps = propertyCatalog.reduce<Record<PropertyGroup, PropertyDefinition[]>>((acc, p) => {
    if (!acc[p.group]) acc[p.group] = [];
    acc[p.group]!.push(p);
    return acc;
  }, {} as Record<PropertyGroup, PropertyDefinition[]>);

  const handlePropertyChange = (propId: string) => {
    const prop = propertyCatalog.find(p => p.id === propId);
    if (!prop) return;
    const ops = getOperatorsForType(prop.type);
    const newOp = ops[0];
    onChange({
      ...condition,
      propertyId: propId,
      operator: newOp?.id ?? condition.operator,
      value: undefined,
      secondValue: undefined,
    });
    setPropertyOpen(false);
  };

  const handleOperatorChange = (opId: string) => {
    const op = applicableOps.find(o => o.id === opId);
    onChange({
      ...condition,
      operator: opId,
      value: op?.requiresValue ? condition.value : undefined,
      secondValue: op?.requiresSecondValue ? condition.secondValue : undefined,
    });
  };

  const renderValueInput = () => {
    if (!selectedOp?.requiresValue) return null;

    if (selectedProp?.type === 'picklist' && selectedProp.picklistValues) {
      return (
        <Select value={String(condition.value ?? '')} onValueChange={(v) => onChange({ ...condition, value: v })}>
          <SelectTrigger className="h-7 text-xs w-[140px] bg-background">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {selectedProp.picklistValues.map(v => (
              <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (selectedProp?.type === 'boolean') return null;

    if (selectedProp?.type === 'number' || selectedProp?.type === 'currency') {
      return (
        <div className="flex items-center gap-1">
          {selectedProp.type === 'currency' && <span className="text-xs text-muted-foreground">$</span>}
          <Input
            type="number"
            value={condition.value !== undefined ? String(condition.value) : ''}
            onChange={(e) => onChange({ ...condition, value: e.target.value ? Number(e.target.value) : undefined })}
            className="h-7 text-xs w-[100px] bg-background"
            placeholder="0"
          />
          {selectedOp?.requiresSecondValue && (
            <>
              <span className="text-xs text-muted-foreground">and</span>
              {selectedProp.type === 'currency' && <span className="text-xs text-muted-foreground">$</span>}
              <Input
                type="number"
                value={condition.secondValue !== undefined ? String(condition.secondValue) : ''}
                onChange={(e) => onChange({ ...condition, secondValue: e.target.value ? Number(e.target.value) : undefined })}
                className="h-7 text-xs w-[100px] bg-background"
                placeholder="0"
              />
            </>
          )}
        </div>
      );
    }

    if (selectedProp?.type === 'date') {
      if (condition.operator === 'within_last' || condition.operator === 'more_than_ago') {
        return (
          <div className="flex items-center gap-1">
            <Input
              type="number"
              value={condition.value !== undefined ? String(condition.value) : ''}
              onChange={(e) => onChange({ ...condition, value: e.target.value ? Number(e.target.value) : undefined })}
              className="h-7 text-xs w-[80px] bg-background"
              placeholder="0"
            />
            <span className="text-xs text-muted-foreground">days</span>
          </div>
        );
      }
      return (
        <Input
          type="date"
          value={String(condition.value ?? '')}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          className="h-7 text-xs w-[140px] bg-background"
        />
      );
    }

    return (
      <Input
        type="text"
        value={String(condition.value ?? '')}
        onChange={(e) => onChange({ ...condition, value: e.target.value })}
        className="h-7 text-xs w-[160px] bg-background"
        placeholder="Value"
      />
    );
  };

  return (
    <div className="flex items-center gap-2 group">
      <Popover open={propertyOpen} onOpenChange={setPropertyOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={propertyOpen}
            className="h-7 text-xs justify-between w-[180px] bg-background font-normal"
          >
            <span className="truncate">{selectedProp?.label ?? 'Select property...'}</span>
            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search properties..." className="h-9 text-xs" />
            <CommandList>
              <CommandEmpty className="text-xs">No property found.</CommandEmpty>
              {(Object.entries(groupedProps) as [PropertyGroup, PropertyDefinition[]][]).map(([group, props]) => (
                <CommandGroup key={group} heading={propertyGroupLabels[group]}>
                  {props.map(p => (
                    <CommandItem
                      key={p.id}
                      value={p.label}
                      onSelect={() => handlePropertyChange(p.id)}
                      className="text-xs"
                    >
                      <Check className={cn("mr-2 h-3 w-3", condition.propertyId === p.id ? "opacity-100" : "opacity-0")} />
                      <span>{p.label}</span>
                      <span className="ml-auto text-[10px] text-muted-foreground capitalize">{p.type}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Select value={condition.operator} onValueChange={handleOperatorChange}>
        <SelectTrigger className="h-7 text-xs w-[170px] bg-background">
          <SelectValue placeholder="Operator" />
        </SelectTrigger>
        <SelectContent>
          {applicableOps.map(op => (
            <SelectItem key={op.id} value={op.id} className="text-xs">{op.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {renderValueInput()}

      <Button
        variant="ghost"
        size="icon"
        className={cn("h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive", isOnly && "invisible")}
        onClick={onRemove}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}
